import {
  Workspace,
  type CaseTypeRepository,
  type OrganizationRepository,
  type WorkspaceRepository,
  type AffiliateRepository,
} from '@telespot/domain';
import { type Command, type CommandHandler } from '@telespot/core';

/**
 * @group Workspaces
 * @category Payloads
 */
export interface CreateWorkspaceDetail {
  name: string;
  organizationId: string;
  caseTypeId: string;
  createdById: string;
  config: any;
}

/**
 * @group Workspaces
 * @category Tokens
 */
export const CREATE_WORKSPACE = 'workspaces:create';

/**
 * @group Workspaces
 * @category Commands
 */
export class CreateWorkspace implements Command<typeof CREATE_WORKSPACE, CreateWorkspaceDetail> {
  public readonly name = CREATE_WORKSPACE;

  constructor(public readonly details: Readonly<CreateWorkspaceDetail>) {}
}

/**
 * @group Workspaces
 * @category Handlers
 */
export type ICreateWorkspaceHandler = CommandHandler<CreateWorkspace>;

/**
 * @group Workspaces
 * @category Errors
 */
export enum CreateWorkspaceError {
  USER_AFFILIATION_ERROR = 'User does has no permissions to create workspace for organization',
  INVALID_CASE_TYPE = 'CaseType not found',
  DUPLICATED_WORKSPACE = 'Workspace duplication error', // REVIEW: when and if this error  should be thrown
  ORGANIZATION_NOT_FOUND = 'Organization not found',
}

/**
 * @group Workspaces
 * @category Handlers
 */
export class CreateWorkspaceHandler implements ICreateWorkspaceHandler {
  constructor(
    private readonly workspaces: WorkspaceRepository,
    private readonly organizations: OrganizationRepository,
    private readonly affiliates: AffiliateRepository,
    private readonly caseTypes: CaseTypeRepository
  ) {}

  public async execute(command: CreateWorkspace): Promise<string> {
    const { name, organizationId, caseTypeId, createdById, config } = command.details;

    const affiliation = await this.affiliates.findInOrganization(organizationId, createdById);

    if (!affiliation) throw new Error(CreateWorkspaceError.USER_AFFILIATION_ERROR);

    const organization = await this.organizations.getById(organizationId);

    if (!organization) throw new Error(CreateWorkspaceError.ORGANIZATION_NOT_FOUND);

    const caseType = await this.caseTypes.getById(caseTypeId);

    if (!caseType) throw new Error(CreateWorkspaceError.INVALID_CASE_TYPE);

    const workspace = new Workspace({
      usedStorage: 0,
      numCases: 0,
      numMembers: 0,
      caseTypeId: caseType.id,
      config,
      createdById: affiliation.user.id,
      name,
      organizationId: organization.id,
    });

    return this.workspaces.save(workspace);
  }
}
